import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, DollarSign, Phone, TrendingUp, Plus, Mic } from 'lucide-react';
import api from '../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    balance: 0,
    agents: 0,
    calls: 0,
    totalCost: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [balanceRes, callStatsRes, agentsRes] = await Promise.all([
        api.getCreditBalance(),
        api.getCallStats(30),
        api.getAgents({ is_active: true })
      ]);

      setStats({
        balance: balanceRes.data.balance || 0,
        agents: agentsRes.data.agents?.length || 0,
        calls: callStatsRes.data.total_calls || 0,
        totalCost: callStatsRes.data.total_cost || 0
      });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      name: 'Credit Balance',
      value: `$${stats.balance.toFixed(2)}`,
      icon: DollarSign,
      color: 'bg-green-500',
      link: '/credits'
    },
    {
      name: 'Active Agents',
      value: stats.agents,
      icon: Bot,
      color: 'bg-blue-500',
      link: '/agents'
    },
    {
      name: 'Total Calls (30d)',
      value: stats.calls,
      icon: Phone,
      color: 'bg-purple-500',
      link: '/calls'
    },
    {
      name: 'Total Cost (30d)',
      value: `$${stats.totalCost.toFixed(2)}`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      link: '/analytics/costs'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">Voice agent management overview</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.name}
              to={stat.link}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center">
                  <div className={`flex-shrink-0 p-3 rounded-lg ${stat.color}`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 ml-5">
                    <p className="text-sm font-medium text-gray-500 truncate">{stat.name}</p>
                    <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Link
            to="/agents/new"
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 transition-colors text-center group"
          >
            <Plus className="w-8 h-8 mx-auto text-gray-400 group-hover:text-primary-500 mb-2" />
            <span className="text-sm font-medium text-gray-900">Create Voice Agent</span>
          </Link>
          <Link
            to="/test"
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 transition-colors text-center group"
          >
            <Mic className="w-8 h-8 mx-auto text-gray-400 group-hover:text-primary-500 mb-2" />
            <span className="text-sm font-medium text-gray-900">Test Voice Call</span>
          </Link>
          <Link
            to="/calls"
            className="p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 transition-colors text-center group"
          >
            <Phone className="w-8 h-8 mx-auto text-gray-400 group-hover:text-primary-500 mb-2" />
            <span className="text-sm font-medium text-gray-900">View Call Logs</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
